/**
 * Version manager module
 *
 * This module:
 * - Handles asset versioning
 * - Manages cache invalidation based on versions
 * - Provides utilities for working with versioned assets
 */
import { CacheManifest } from "../config/types";
import { CACHE_MANIFEST_PATH } from "../config/constants";
import { debugLog } from "../utils/cache-helpers";
import {
  addVersionToUrl,
  extractVersionFromUrl,
  generateSimpleHash,
} from "../utils/hash-generator";
import { CacheManager } from "./cache-manager";

/**
 * Version manager class
 */
export class VersionManager {
  private static deploymentId: string = "";

  /**
   * Initialize the version manager
   * @param deploymentId Optional deployment ID for versioning
   */
  static init(deploymentId?: string): void {
    // Generate a deployment ID if not provided
    this.deploymentId = deploymentId || this.generateDeploymentId();
    debugLog(
      "Version manager initialized with deployment ID:",
      this.deploymentId
    );
  }

  /**
   * Generate a deployment ID
   * @returns Deployment ID
   */
  private static generateDeploymentId(): string {
    // Use the current timestamp as a simple deployment ID
    return Date.now().toString(36);
  }

  /**
   * Get the current deployment ID
   * @returns Deployment ID
   */
  static getDeploymentId(): string {
    return this.deploymentId;
  }

  /**
   * Create a versioned URL for an asset
   * @param url URL of the asset
   * @param version Optional version string (defaults to deployment ID)
   * @returns Versioned URL
   */
  static createVersionedUrl(url: string, version?: string): string {
    const versionToUse = version || this.deploymentId;
    return addVersionToUrl(url, versionToUse);
  }

  /**
   * Check if an asset needs to be updated
   * @param url URL of the asset
   * @param version Current version of the asset
   * @returns Whether the asset needs to be updated
   */
  static async needsUpdate(url: string, version: string): Promise<boolean> {
    // Get the asset metadata from the cache manifest
    const metadata = await CacheManager.getAssetMetadata(url);

    if (!metadata) {
      // Asset not in cache, needs update
      return true;
    }

    // Compare versions
    return metadata.version !== version;
  }

  /**
   * Update the version of an asset in the cache manifest
   * @param url URL of the asset
   * @param version New version of the asset
   */
  static async updateVersion(url: string, version: string): Promise<void> {
    // Get the asset metadata from the cache manifest
    const metadata = await CacheManager.getAssetMetadata(url);

    if (metadata) {
      // Update the version
      metadata.version = version;
      metadata.lastModified = new Date().toISOString();

      // Update the manifest
      await CacheManager.addToManifest(url, metadata);
    }
  }

  /**
   * Generate a content-based version for an asset
   * @param content Asset content
   * @returns Content hash
   */
  static async generateContentVersion(content: Blob | string): Promise<string> {
    if (typeof content === "string") {
      return generateSimpleHash(content);
    } else {
      return await this.generateContentHash(content);
    }
  }

  /**
   * Generate a content hash from a Blob
   * @param content Blob to hash
   * @returns Content hash
   */
  private static async generateContentHash(content: Blob): Promise<string> {
    // Use the Web Crypto API if available
    if (typeof crypto !== "undefined" && crypto.subtle) {
      try {
        const buffer = await content.arrayBuffer();
        const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
        return hashHex.slice(0, 8); // Use first 8 characters for brevity
      } catch (error) {
        // Fall back to simple hash
        return this.generateSimpleHashFromBlob(content);
      }
    }

    // Fall back to simple hash if Web Crypto API is not available
    return this.generateSimpleHashFromBlob(content);
  }

  /**
   * Generate a simple hash from a Blob
   * @param content Blob to hash
   * @returns Simple hash
   */
  private static async generateSimpleHashFromBlob(
    content: Blob
  ): Promise<string> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(generateSimpleHash(result).slice(0, 8));
      };
      reader.readAsText(content);
    });
  }

  /**
   * Check if a URL has a version parameter
   * @param url URL to check
   * @returns Whether the URL has a version parameter
   */
  static hasVersion(url: string): boolean {
    return extractVersionFromUrl(url) !== null;
  }

  /**
   * Extract the version from a URL
   * @param url URL to extract version from
   * @returns Version string or null if not found
   */
  static getVersionFromUrl(url: string): string | null {
    return extractVersionFromUrl(url);
  }

  /**
   * Invalidate all assets from a previous deployment
   * @param previousDeploymentId Previous deployment ID
   */
  static async invalidatePreviousDeployment(
    previousDeploymentId: string
  ): Promise<void> {
    try {
      // Get all assets from the cache manifest
      const manifest = await this.getManifest();

      if (!manifest) {
        return;
      }

      // Find assets with the previous deployment ID
      const assetsToInvalidate = Object.values(manifest.assets).filter(
        (asset) => asset.version.startsWith(previousDeploymentId)
      );

      // Invalidate each asset
      for (const asset of assetsToInvalidate) {
        await CacheManager.removeFromManifest(asset.url);
      }

      debugLog(
        `Invalidated ${assetsToInvalidate.length} assets from previous deployment`
      );
    } catch (error) {
      debugLog("Failed to invalidate previous deployment:", error);
    }
  }

  /**
   * Get the cache manifest
   * @returns Cache manifest or null if not available
   */
  private static async getManifest(): Promise<CacheManifest | null> {
    try {
      // Try to get the manifest from the cache
      const cache = await caches.open("asset-cache-v1");
      const manifestResponse = await cache.match(CACHE_MANIFEST_PATH);

      if (manifestResponse) {
        return await manifestResponse.json();
      }

      return null;
    } catch (error) {
      debugLog("Failed to get manifest:", error);
      return null;
    }
  }

  /**
   * Compare two versions
   * @param version1 First version
   * @param version2 Second version
   * @returns -1 if version1 < version2, 0 if equal, 1 if version1 > version2
   */
  static compareVersions(version1: string, version2: string): number {
    // If versions are timestamps in base36, convert to numbers
    if (/^[0-9a-z]+$/.test(version1) && /^[0-9a-z]+$/.test(version2)) {
      const num1 = parseInt(version1, 36);
      const num2 = parseInt(version2, 36);

      if (!isNaN(num1) && !isNaN(num2)) {
        return num1 - num2;
      }
    }

    // Otherwise, compare as strings
    return version1.localeCompare(version2);
  }
}
